import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  ChevronLeft, Plus, Trash2, X, CheckCircle, AlertCircle,
  Scale, Activity, TrendingDown
} from "lucide-react";
import { LineChart, Line, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import BottomNav from "../../components/ui/BottomNav";

const API = process.env.REACT_APP_BACKEND_URL;

const METING_VELDEN = [
  { key: "gewicht",     label: "Gewicht",     unit: "kg",  icon: "⚖️" },
  { key: "lichaamsvet", label: "Lichaamsvet",  unit: "%",   icon: "📊" },
  { key: "taille",      label: "Taille",       unit: "cm",  icon: "📏" },
  { key: "heup",        label: "Heup",         unit: "cm",  icon: "📏" },
  { key: "borst",       label: "Borst",        unit: "cm",  icon: "📏" },
  { key: "bovenbeen",   label: "Bovenbeen",    unit: "cm",  icon: "📏" },
  { key: "bovenarm",    label: "Bovenarm",     unit: "cm",  icon: "📏" },
];

function AddMeetingModal({ onClose, onSave, token }) {
  const [form, setForm] = useState({ datum: new Date().toISOString().split("T")[0] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const inputCls = "bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50 w-full placeholder-zinc-700";

  const handleSave = async () => {
    const hasData = METING_VELDEN.some(f => form[f.key]);
    if (!hasData) { setError("Vul minimaal één meting in"); return; }
    setSaving(true); setError("");
    try {
      const r = await fetch(`${API}/api/nutrition/lichaam`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Opslaan mislukt");
      onSave();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl w-full max-w-sm max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/8">
          <h3 className="text-white font-bold">Meting invoeren</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-1.5 block">Datum</label>
            <input type="date" value={form.datum} onChange={e => setForm(f => ({ ...f, datum: e.target.value }))}
              className={inputCls} max={new Date().toISOString().split("T")[0]} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {METING_VELDEN.map(({ key, label, unit }) => (
              <div key={key}>
                <label className="text-zinc-500 text-xs font-semibold mb-1 block">{label} ({unit})</label>
                <input type="number" value={form[key] || ""} min={0} step={0.1}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  data-testid={`meting-${key}-input`}
                  className={inputCls} placeholder="—" />
              </div>
            ))}
          </div>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-white/10 text-zinc-400 text-sm font-semibold">Annuleren</button>
            <button onClick={handleSave} disabled={saving} data-testid="save-meting-btn"
              className="flex-1 py-2.5 rounded-xl text-black text-sm font-bold disabled:opacity-40"
              style={{ background: "#C9A84C" }}>
              {saving ? "..." : "Opslaan"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LichaamPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    if (!user?.token) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/nutrition/lichaam`, { headers: { Authorization: `Bearer ${user.token}` } });
      if (r.ok) setLogs((await r.json()).logs || []);
    } catch {}
    setLoading(false);
  }, [user?.token]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    try {
      const r = await fetch(`${API}/api/nutrition/lichaam/${id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${user.token}` }
      });
      if (!r.ok) throw new Error("Verwijderen mislukt");
      setLogs(prev => prev.filter(l => l.id !== id));
      showToast("Meting verwijderd");
    } catch (e) { showToast(e.message, "error"); }
  };

  // Latest values
  const latest = logs[0] || {};
  const oldest = logs[logs.length - 1] || {};

  // Weight chart (last 20 entries with gewicht)
  const weightData = logs
    .filter(l => l.gewicht)
    .slice(0, 20)
    .reverse()
    .map(l => ({
      datum: new Date(l.datum).toLocaleDateString("nl-NL", { day: "numeric", month: "short" }),
      gewicht: parseFloat(l.gewicht),
    }));

  const deltaGewicht = latest.gewicht && oldest.gewicht
    ? Math.round((latest.gewicht - oldest.gewicht) * 10) / 10 : null;

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <div className="max-w-2xl mx-auto px-4 pt-5 pb-28">
        {/* Back */}
        <button onClick={() => navigate("/account")}
          className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm font-semibold mb-6 transition-colors">
          <ChevronLeft size={16} /> Profiel
        </button>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-white font-black text-2xl">Lichaam</h1>
          <button onClick={() => setShowModal(true)} data-testid="add-meting-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-black text-sm font-bold"
            style={{ background: "#C9A84C" }}>
            <Plus size={14} /> Meting
          </button>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2].map(i => <div key={i} className="h-24 bg-[#111] border border-white/8 rounded-2xl animate-pulse" />)}
          </div>
        ) : logs.length === 0 ? (
          <div className="bg-[#111] border border-white/8 rounded-2xl p-10 text-center">
            <Scale size={32} className="text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 font-semibold mb-1">Nog geen metingen</p>
            <p className="text-zinc-600 text-sm mb-4">Voeg je eerste meting toe om je voortgang bij te houden</p>
            <button onClick={() => setShowModal(true)}
              className="px-5 py-2.5 rounded-xl text-black font-bold text-sm"
              style={{ background: "#C9A84C" }}>
              Eerste meting toevoegen
            </button>
          </div>
        ) : (
          <>
            {/* Current stats */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {latest.gewicht && (
                <div className="bg-[#111] border border-white/8 rounded-2xl p-4 col-span-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-zinc-500 text-xs font-semibold uppercase tracking-wide mb-1">Huidig gewicht</p>
                      <p className="text-white font-black text-3xl tabular-nums">{latest.gewicht} <span className="text-zinc-600 text-lg font-normal">kg</span></p>
                    </div>
                    {deltaGewicht !== null && logs.length > 1 && (
                      <div className={`flex items-center gap-1 px-2.5 py-1 rounded-xl text-sm font-bold ${deltaGewicht < 0 ? "bg-green-500/10 text-green-400" : deltaGewicht > 0 ? "bg-red-500/10 text-red-400" : "bg-zinc-800 text-zinc-400"}`}>
                        <TrendingDown size={13} />
                        {deltaGewicht > 0 ? "+" : ""}{deltaGewicht} kg
                      </div>
                    )}
                  </div>
                </div>
              )}
              {[
                { key: "lichaamsvet", label: "Lichaamsvet", unit: "%" },
                { key: "taille", label: "Taille", unit: "cm" },
                { key: "heup", label: "Heup", unit: "cm" },
                { key: "borst", label: "Borst", unit: "cm" },
                { key: "bovenbeen", label: "Bovenbeen", unit: "cm" },
                { key: "bovenarm", label: "Bovenarm", unit: "cm" },
              ].filter(m => latest[m.key]).map(m => (
                <div key={m.key} className="bg-[#111] border border-white/8 rounded-2xl p-4">
                  <p className="text-zinc-500 text-xs mb-1">{m.label}</p>
                  <p className="text-white font-black text-xl tabular-nums">
                    {latest[m.key]} <span className="text-zinc-600 text-sm font-normal">{m.unit}</span>
                  </p>
                </div>
              ))}
            </div>

            {/* Weight chart */}
            {weightData.length >= 2 && (
              <div className="account-card mb-6">
                <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-4">Gewichtsverloop</p>
                <ResponsiveContainer width="100%" height={130}>
                  <LineChart data={weightData} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                    <XAxis dataKey="datum" tick={{ fill: "#666", fontSize: 10 }} axisLine={false} tickLine={false}
                      interval="preserveStartEnd" />
                    <Tooltip
                      contentStyle={{ background: "#111", border: "1px solid #333", borderRadius: 10, fontSize: 12 }}
                      formatter={v => [`${v} kg`, "Gewicht"]}
                      labelStyle={{ color: "#C9A84C", fontWeight: "bold" }}
                    />
                    <Line type="monotone" dataKey="gewicht" stroke="#C9A84C" strokeWidth={2}
                      dot={{ fill: "#C9A84C", r: 3 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* History */}
            <div className="account-card">
              <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-4">
                Geschiedenis ({logs.length})
              </p>
              <div className="space-y-3">
                {logs.map(l => (
                  <div key={l.id} data-testid={`meting-item-${l.id}`}
                    className="flex items-start gap-3 bg-[#1a1a1a] rounded-xl p-3.5">
                    <div className="flex-1 min-w-0">
                      <p className="text-zinc-300 text-sm font-semibold mb-1.5">
                        {new Date(l.datum).toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "long" })}
                      </p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        {METING_VELDEN.filter(f => l[f.key]).map(f => (
                          <span key={f.key} className="text-xs text-zinc-500">
                            {f.label}: <span className="text-zinc-300 font-semibold">{l[f.key]} {f.unit}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                    <button onClick={() => handleDelete(l.id)} data-testid={`delete-meting-${l.id}`}
                      className="text-zinc-700 hover:text-red-400 transition-colors p-1 shrink-0">
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Toast */}
        {toast && (
          <div className={`fixed bottom-20 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-xs z-50 flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold shadow-2xl ${toast.type === "success" ? "bg-[#111] border border-[#C9A84C]/40 text-white" : "bg-red-950 border border-red-500/40 text-red-300"}`}>
            {toast.type === "success" ? <CheckCircle size={14} className="text-[#C9A84C]" /> : <AlertCircle size={14} className="text-red-400" />}
            {toast.msg}
          </div>
        )}
      </div>

      {showModal && (
        <AddMeetingModal token={user.token} onClose={() => setShowModal(false)} onSave={() => { setShowModal(false); load(); showToast("Meting opgeslagen!"); }} />
      )}
      <BottomNav />
    </div>
  );
}
